import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const functions = getFunctions();
const createCheckout = httpsCallable(functions, 'createCheckout');
const customerPortal = httpsCallable(functions, 'customerPortal');
const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');

const isNative = Capacitor.isNativePlatform();

const FEATURES = [
  'GPS mileage tracking',
  'Auto-drive detection',
  'Manual trip entry',
  'IRS deduction calculator',
  'Receipt scanning & OCR',
  'Expense tracking',
  'Trip history & reports',
  'Up to 2 vehicles',
  'CSV export',
];

export default function Subscription() {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    async function fetchSubscription() {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists()) {
        setSubscription(snap.data().subscription || {});
      }
      setLoading(false);
    }
    fetchSubscription();
  }, [currentUser.uid]);

  // Re-fetch subscription status when returning from Stripe checkout on native
  useEffect(() => {
    if (!isNative) return;
    let listener;
    async function setup() {
      const { Browser } = await import('@capacitor/browser');
      listener = await Browser.addListener('browserFinished', async () => {
        try {
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          if (snap.exists()) {
            setSubscription(snap.data().subscription || {});
          }
        } catch (err) {
          console.error('Error refreshing subscription:', err);
        }
      });
    }
    setup();
    return () => { if (listener) listener.remove(); };
  }, [currentUser.uid]);

  async function handleSubscribe() {
    setCheckoutLoading(true);
    try {
      const result = await createCheckout({ plan: 'standard' });
      if (result.data.url) {
        if (isNative) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: result.data.url });
        } else {
          window.location.href = result.data.url;
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err.message || 'Failed to start checkout');
    }
    setCheckoutLoading(false);
  }

  async function handleManageSubscription() {
    try {
      const result = await customerPortal();
      if (result.data.url) {
        if (isNative) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: result.data.url });
        } else {
          window.location.href = result.data.url;
        }
      }
    } catch (err) {
      console.error('Portal error:', err);
    }
  }

  async function handleCancelSubscription() {
    setCancelLoading(true);
    try {
      await cancelSubscriptionFn();
      setSubscription((prev) => ({ ...prev, cancelAtPeriodEnd: true }));
      setShowCancelModal(false);
    } catch (err) {
      console.error('Cancel error:', err);
      alert(err.message || 'Failed to cancel subscription');
    }
    setCancelLoading(false);
  }

  if (loading) {
    return <div className="screen-container"><div className="loading">Loading...</div></div>;
  }

  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isTrialing = subscription?.status === 'trialing';
  const isCanceling = subscription?.cancelAtPeriodEnd;

  return (
    <div className="screen-container">
      <h2>Subscription</h2>

      {isActive ? (
        <div className="subscription-active">
          <div className="subscription-status-card">
            <div className={`subscription-badge ${isCanceling ? 'canceling' : 'active'}`}>
              {isCanceling ? 'Canceling' : isTrialing ? 'Free Trial' : 'Active'}
            </div>
            <h3>MileDeduct</h3>
            <p className="subscription-price-display">$39.99/year</p>
            {isCanceling && subscription.currentPeriodEnd && (
              <p className="subscription-meta">
                Access until {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric'
                })}
              </p>
            )}
            {!isCanceling && isTrialing && subscription.trialEnd && (
              <p className="subscription-meta">
                Trial ends {new Date(subscription.trialEnd).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric'
                })}
              </p>
            )}
            {!isCanceling && !isTrialing && subscription.currentPeriodEnd && (
              <p className="subscription-meta">
                Renews {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric'
                })}
              </p>
            )}
          </div>

          {!isCanceling && (
            <div className="subscription-actions">
              {!isNative && (
                <button className="btn btn-secondary" onClick={handleManageSubscription}>
                  Manage Subscription
                </button>
              )}
              <button className="btn btn-danger-outline" onClick={() => setShowCancelModal(true)}>
                Cancel Subscription
              </button>
            </div>
          )}

          {isCanceling && !isNative && (
            <button className="btn btn-secondary" onClick={handleManageSubscription}>
              Manage Subscription
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="single-plan-card">
            <div className="single-plan-header">
              <img src={isNative ? './milededuct-logo.svg' : '/favicon.svg'} alt="" className="single-plan-icon" />
              <div>
                <h3 className="plan-name">MileDeduct</h3>
                <p className="single-plan-tagline">Full access. Every feature. One price.</p>
              </div>
            </div>

            <div className="plan-price" style={{ textAlign: 'center', margin: '24px 0' }}>
              <span className="plan-amount">$39.99</span>
              <span className="plan-period">/year</span>
              <div className="plan-price-note">That's less than $0.77/week</div>
            </div>

            <ul className="plan-features">
              {FEATURES.map((f, i) => (
                <li key={i}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="#0D9488">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  {f}
                </li>
              ))}
            </ul>

            <button
              className="btn btn-primary"
              onClick={handleSubscribe}
              disabled={checkoutLoading}
              style={{ marginTop: '8px' }}
            >
              {checkoutLoading ? 'Loading...' : 'Start 30-Day Free Trial'}
            </button>
          </div>

          <p className="subscription-disclaimer">
            Your card will be charged $39.99 after the 30-day trial ends. All fees are non-refundable.
            By subscribing you agree to our <a href={isNative ? '#/terms' : '/terms'}>Terms of Service</a>.
          </p>
        </>
      )}

      {showCancelModal && (
        <div className="modal-overlay" onClick={() => !cancelLoading && setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>
              {isTrialing
                ? 'Are you sure you want to cancel your free trial? You\'ll lose access to premium features immediately after your trial ends.'
                : 'Are you sure? You\'ll keep access until the end of your current billing period, but your subscription won\'t renew.'}
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
              >
                Keep Subscription
              </button>
              <button
                className="btn btn-danger"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
